/* Particulier verkoop-wizard
   Flow: Basis → Kenmerken → Foto's → Contact → Preview (reuse public layout).
   The "Advertentie indienen" button on the preview step is the ONLY way to
   submit; the backend forces moderation_status="pending" regardless.
   Data is persisted on every "Volgende" so going back-and-forth never loses
   information or photos. */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2, Check, Upload, HomeIcon, Eye, Save,
} from "lucide-react";
import { toast } from "sonner";
import { api, compressImageFile, shrinkGallery } from "../../lib/api";
import { SortableGallery } from "../../components/gallery/SortableGallery";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../context/AuthContext";
import { ListingPreview } from "../../components/koop/ListingPreview";
import { TERMS_VERSION } from "../legal/LegalPage";

const DECLARATION_VERSION = "v1";

const STEPS = ["Basis", "Kenmerken", "Foto's", "Contact", "Controleer"];
const TYPES = ["huis", "appartement", "studio", "penthouse", "villa", "bungalow", "overig"];
const ENERGY = ["A", "B", "C", "D", "E", "F", "G", "—"];

const initial = {
  title: "", street: "", house_number: "", house_number_addition: "", postcode: "", city: "", province: "",
  property_type: "huis", price: 0, price_condition: "kk", lat: null, lng: null,
  m2: 0, plot_m2: null, volume_m3: null, rooms: null, bedrooms: 0, bathrooms: null, floors: null, year_built: null,
  energy_label: "—", heating: null, hot_water: null, insulation: null,
  balcony: false, garden: "", garage: false, parking: false, storage: false, elevator: false, accessibility: null,
  ownership: null, leasehold: null, vve: null, vve_contribution: null, service_costs: null, monument: null, special_notes: null,
  description: "", features: [], image: null, gallery: [], floorplans: [], video_url: "",
  status: "concept", available_from: "In overleg", delivery: null, viewings_allowed: true,
  seller_name: "", seller_email: "", seller_phone: "", contact_preference: "email",
};

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-sm font-semibold text-navy">{label}</Label>
    {children ? <div className="mt-1.5">{children}</div> : null}
    {hint ? <p className="mt-1 text-xs text-slate-400">{hint}</p> : null}
  </div>
);
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const VerkoopWizard = () => {
  const { lid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [f, setF] = useState(initial);
  const [id, setId] = useState(lid || null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(!!lid);
  const [declaration, setDeclaration] = useState(false);
  const dirty = useRef(false);
  const set = (k, v) => { dirty.current = true; setF((p) => ({ ...p, [k]: v })); };

  useEffect(() => {
    if (!lid) {
      // Prefill contact details from account if present
      setF((p) => ({
        ...p,
        seller_name: p.seller_name || user?.name || "",
        seller_email: p.seller_email || user?.email || "",
      }));
      return;
    }
    api(`/particulier/sale-listings/${lid}`)
      .then((d) => setF({ ...initial, ...d, gallery: d.gallery || [] }))
      .catch(() => toast.error("Kon advertentie niet laden"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lid]);

  const payload = () => ({
    ...f,
    price: Number(f.price) || 0,
    m2: Number(f.m2) || 0,
    bedrooms: Number(f.bedrooms) || 0,
    plot_m2: numOrNull(f.plot_m2),
    rooms: numOrNull(f.rooms),
    bathrooms: numOrNull(f.bathrooms),
    floors: numOrNull(f.floors),
    year_built: numOrNull(f.year_built),
    volume_m3: numOrNull(f.volume_m3),
    vve_contribution: numOrNull(f.vve_contribution),
    service_costs: numOrNull(f.service_costs),
    lat: numOrNull(f.lat),
    lng: numOrNull(f.lng),
    image: f.image || (f.gallery && f.gallery[0]) || null,
  });

  const save = async (silent = false) => {
    if (!f.street.trim() || !f.city.trim()) {
      if (!silent) toast.error("Vul minimaal straat en plaats in.");
      return null;
    }
    setSaving(true);
    try {
      // Shrink any oversized gallery entries (legacy or re-opened draft) before
      // saving so we never breach MongoDB's 16 MB BSON document limit.
      const safeGallery = await shrinkGallery(f.gallery || []);
      const body = { ...payload(), gallery: safeGallery, image: safeGallery[0] || null };
      let res;
      if (id) res = await api(`/particulier/sale-listings/${id}`, { method: "PATCH", body: JSON.stringify(body) });
      else {
        res = await api("/particulier/sale-listings", { method: "POST", body: JSON.stringify(body) });
        setId(res.id);
        // reflect new slug for later preview
        if (res.slug) setF((p) => ({ ...p, slug: res.slug }));
      }
      // reflect compressed gallery back into form state so subsequent saves don't repeat the work
      setF((p) => ({ ...p, gallery: safeGallery, image: safeGallery[0] || null }));
      dirty.current = false;
      if (!silent) toast.success("Opgeslagen");
      return res;
    } catch (e) { toast.error(e.message); return null; } finally { setSaving(false); }
  };

  const validate = (which = step) => {
    if (which === 0) {
      if (!f.street.trim() || !f.city.trim()) return "Straat en plaats zijn verplicht.";
      if (!f.price || Number(f.price) <= 0) return "Vul een vraagprijs in.";
    }
    if (which === 3) {
      if (!f.seller_name?.trim()) return "Vul je naam in zodat geïnteresseerden je kunnen bereiken.";
      if (!f.seller_email?.trim() && !f.seller_phone?.trim()) return "Vul minimaal e-mail of telefoon in.";
    }
    return null;
  };
  const next = async () => {
    const err = validate(); if (err) return toast.error(err);
    if (dirty.current) await save(true);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const uploadPhotos = async (files) => {
    const current = f.gallery || [];
    const room = Math.max(0, 40 - current.length);
    if (room === 0) { toast.error("Maximaal 40 foto's per advertentie."); return; }
    const arr = Array.from(files).slice(0, room);
    try {
      const encoded = await Promise.all(arr.map((file) => compressImageFile(file)));
      set("gallery", [...current, ...encoded]);
      // auto-save photos so they are not lost when stepping back
      setTimeout(() => save(true), 0);
    } catch { toast.error("Uploaden mislukt"); }
  };
  const removePhoto = (i) => set("gallery", f.gallery.filter((_, idx) => idx !== i));

  const submit = async () => {
    const bad = validate(0) || validate(3);
    if (bad) return toast.error(bad);
    if (!declaration) {
      return toast.error("Vink de verklaring aan vóór je de advertentie indient.");
    }
    setSubmitting(true);
    try {
      const res = await save(true);
      if (!res && !id) return;
      // Backend requires the declaration + terms version on the activation call.
      await api(`/particulier/sale-listings/${id || res.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({
          status: "actief",
          seller_declaration_accepted: true,
          seller_declaration_version: DECLARATION_VERSION,
          terms_version_accepted: TERMS_VERSION,
        }),
      });
      navigate(`/mijn-woning-verkopen/ingediend/${id || res.id}`);
    } catch (e) {
      toast.error(e.message);
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-brandblue" /></div>;
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-navy" data-testid="verkoop-wizard-title">
            {id ? "Verkoop-advertentie bewerken" : "Verkoop je woning"}
          </h1>
          <p className="text-sm text-slate-500">Als particulier · gratis · 1 actieve advertentie tegelijk</p>
        </div>
        <Button variant="outline" onClick={() => save(false)} disabled={saving} className="rounded-xl" data-testid="save-draft">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={14} className="mr-1.5" /> Tussentijds opslaan</>}
        </Button>
      </div>

      <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <button
            key={s}
            type="button"
            onClick={() => i <= step && setStep(i)}
            className={`flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${
              i === step ? "bg-brandblue text-white" : i < step ? "bg-brandblue-light text-brandblue" : "bg-slate-100 text-slate-400"
            }`}
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
              {i < step ? <Check size={12} /> : i + 1}
            </span>{s}
          </button>
        ))}
      </div>

      {step < 4 && (
        <div className="mt-6 space-y-4 rounded-3xl border border-border bg-white p-6 shadow-sm">
          {step === 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Titel (optioneel)"><Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="bv. Royaal gezinshuis met tuin" className="rounded-xl" /></Field>
              <Field label="Woningtype">
                <Select value={f.property_type} onValueChange={(v) => set("property_type", v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Straat"><Input data-testid="wz-street" value={f.street} onChange={(e) => set("street", e.target.value)} className="rounded-xl" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Huisnummer"><Input value={f.house_number} onChange={(e) => set("house_number", e.target.value)} className="rounded-xl" /></Field>
                <Field label="Toevoeging"><Input value={f.house_number_addition} onChange={(e) => set("house_number_addition", e.target.value)} className="rounded-xl" /></Field>
              </div>
              <Field label="Plaats"><Input data-testid="wz-city" value={f.city} onChange={(e) => set("city", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Postcode"><Input value={f.postcode} onChange={(e) => set("postcode", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Provincie (optioneel)"><Input value={f.province} onChange={(e) => set("province", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Vraagprijs (€)"><Input data-testid="wz-price" type="number" value={f.price || ""} onChange={(e) => set("price", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Prijsconditie">
                <Select value={f.price_condition} onValueChange={(v) => set("price_condition", v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="kk">k.k.</SelectItem><SelectItem value="von">v.o.n.</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Woonopp. (m²)"><Input type="number" value={f.m2 || ""} onChange={(e) => set("m2", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Perceel (m²)"><Input type="number" value={f.plot_m2 ?? ""} onChange={(e) => set("plot_m2", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Inhoud (m³)"><Input type="number" value={f.volume_m3 ?? ""} onChange={(e) => set("volume_m3", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Kamers"><Input type="number" value={f.rooms ?? ""} onChange={(e) => set("rooms", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Slaapkamers"><Input type="number" value={f.bedrooms || ""} onChange={(e) => set("bedrooms", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Badkamers"><Input type="number" value={f.bathrooms ?? ""} onChange={(e) => set("bathrooms", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Woonlagen"><Input type="number" value={f.floors ?? ""} onChange={(e) => set("floors", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Bouwjaar"><Input type="number" value={f.year_built ?? ""} onChange={(e) => set("year_built", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Energielabel">
                <Select value={f.energy_label} onValueChange={(v) => set("energy_label", v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{ENERGY.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Tuin"><Input value={f.garden || ""} onChange={(e) => set("garden", e.target.value)} placeholder="bv. zuid, 60 m²" className="rounded-xl" /></Field>
              <Field label="Beschikbaar vanaf"><Input value={f.available_from} onChange={(e) => set("available_from", e.target.value)} className="rounded-xl" /></Field>
              <Field label="VvE p/m (€)"><Input type="number" value={f.vve_contribution ?? ""} onChange={(e) => set("vve_contribution", e.target.value)} className="rounded-xl" /></Field>
              <div className="sm:col-span-3 grid gap-3 sm:grid-cols-4">
                {[["garage", "Garage"], ["parking", "Parkeerplek"], ["balcony", "Balkon"], ["elevator", "Lift"], ["storage", "Berging"], ["monument", "Monument"]].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 rounded-xl border border-border bg-white p-3 text-sm font-medium text-navy">
                    <Switch checked={!!f[k]} onCheckedChange={(v) => set(k, v)} /> {label}
                  </label>
                ))}
              </div>
              <div className="sm:col-span-3">
                <Field label="Omschrijving">
                  <Textarea rows={6} value={f.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl" placeholder="Beschrijf je woning, de buurt en wat de woning uniek maakt…" />
                </Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold text-navy">Foto&apos;s ({f.gallery?.length || 0} / 40)</Label>
                <p className="mt-1 text-xs text-slate-500">De eerste foto wordt automatisch de hoofdfoto. Sleep om de volgorde aan te passen.</p>
                <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-white py-6 text-sm font-semibold text-brandblue hover:border-brandblue">
                  <Upload size={16} /> Foto&apos;s uploaden
                  <input data-testid="wz-upload" type="file" accept="image/*" multiple onChange={(e) => uploadPhotos(e.target.files)} className="hidden" />
                </label>
                <div className="mt-4">
                  <SortableGallery
                    value={f.gallery || []}
                    onChange={(next) => { set("gallery", next); set("image", next[0] || null); }}
                    onRemove={removePhoto}
                    data-testid="wz-gallery"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Jouw naam"><Input value={f.seller_name || ""} onChange={(e) => set("seller_name", e.target.value)} className="rounded-xl" data-testid="seller-name" /></Field>
              <Field label="Hoe wil je gecontacteerd worden?">
                <Select value={f.contact_preference || "email"} onValueChange={(v) => set("contact_preference", v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Alleen e-mail</SelectItem>
                    <SelectItem value="phone">Alleen telefoon</SelectItem>
                    <SelectItem value="both">E-mail en telefoon</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="E-mail" hint="Verschijnt op de advertentie zodat kopers je kunnen bereiken.">
                <Input type="email" value={f.seller_email || ""} onChange={(e) => set("seller_email", e.target.value)} className="rounded-xl" data-testid="seller-email" />
              </Field>
              <Field label="Telefoon" hint="Optioneel maar aanbevolen.">
                <Input value={f.seller_phone || ""} onChange={(e) => set("seller_phone", e.target.value)} className="rounded-xl" data-testid="seller-phone" />
              </Field>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 rounded-xl border border-border bg-white p-3 text-sm font-medium text-navy">
                  <Switch checked={!!f.viewings_allowed} onCheckedChange={(v) => set("viewings_allowed", v)} /> Ik sta open voor bezichtigingen op afspraak
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="mt-6 space-y-4">
          <div className="rounded-2xl bg-amber-50 p-4 text-sm text-amber-900">
            <strong>Dit is een voorbeeld.</strong> Zo zien bezoekers je advertentie zodra deze door een beheerder is goedgekeurd.
            Alles wat je hier ziet kan je nog aanpassen via "Gegevens aanpassen".
          </div>
          <div className="rounded-3xl border border-border bg-white p-4 shadow-sm sm:p-6">
            <ListingPreview data={{ ...payload(), seller_type: "particulier" }} showContact />
          </div>

          {/* Verplichte verklaring vóór publicatie — v2.0 */}
          <label
            htmlFor="wz-declaration"
            className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 text-sm transition ${declaration ? "border-success bg-success/5" : "border-amber-200 bg-amber-50"}`}
            data-testid="wz-declaration-label"
          >
            <input
              id="wz-declaration"
              type="checkbox"
              checked={declaration}
              onChange={(e) => setDeclaration(e.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-brandblue focus:ring-brandblue"
              data-testid="wz-declaration"
            />
            <span className="leading-relaxed text-slate-700">
              Ik verklaar dat de door mij aangeleverde gegevens en documenten naar mijn beste weten juist, volledig en niet misleidend zijn. Ik begrijp dat WoningAlert deze informatie niet standaard inhoudelijk of juridisch controleert, en ik heb de{" "}
              <a href="/voorwaarden" target="_blank" rel="noreferrer" className="text-brandblue underline">
                algemene voorwaarden (versie {TERMS_VERSION})
              </a>{" "}
              gelezen en geaccepteerd.
            </span>
          </label>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={prev} disabled={step === 0 || submitting} className="rounded-xl">
          ← {step === 4 ? "Gegevens aanpassen" : "Vorige"}
        </Button>
        {step < 4 ? (
          <Button onClick={next} className="rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="wz-next">
            {step === 3 ? <><Eye size={14} className="mr-1.5" /> Controleer advertentie</> : "Volgende"}
          </Button>
        ) : (
          <Button
            onClick={submit}
            disabled={submitting || !declaration}
            className="rounded-xl bg-success hover:bg-success/90 disabled:opacity-50"
            data-testid="wz-submit"
          >
            {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
            Advertentie indienen →
          </Button>
        )}
      </div>
    </div>
  );
};

export default VerkoopWizard;
